import { useState, useEffect, useContext, createContext, useCallback, useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { logger } from "@/infra/logging";
import type { AuthContextType, AuthenticateUserRequest, CurrentUserDto } from "@/shared/types/auth";
import { authService } from "@/features/auth/services/AuthService";
import { authStateManager } from "@/infra/auth/AuthStateManager";
import { ROUTES } from "@/app/router/routes";
import type { SessionExpiredDetail } from "@/infra/auth/auth.events";

const MAX_AUTH_INIT_RETRIES = 3;
const AUTH_INIT_RETRY_DELAY_MS = 3000;

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<CurrentUserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const initAuth = useCallback(async (attempt = 0) => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      setIsLoading(false);
    } catch (error) {
      logger.error("AuthProvider - Failed to fetch current user on init", error as Error, {
        component: "AuthProvider",
        attempt,
        maxRetries: MAX_AUTH_INIT_RETRIES,
      });

      if (attempt < MAX_AUTH_INIT_RETRIES) {
        logger.info(`AuthProvider - Retrying auth init in ${AUTH_INIT_RETRY_DELAY_MS}ms (attempt ${attempt + 1}/${MAX_AUTH_INIT_RETRIES})`, {
          component: "AuthProvider",
        });
        retryTimeoutRef.current = setTimeout(() => void initAuth(attempt + 1), AUTH_INIT_RETRY_DELAY_MS);
      } else {
        logger.warn("AuthProvider - Max auth init retries exhausted, treating as unauthenticated", {
          component: "AuthProvider",
        });
        setUser(null);
        setIsLoading(false);
      }
    } finally {
      if (!authStateManager.isInitialized()) {
        authStateManager.setAuthInitialized();
        logger.debug("AuthProvider - Auth initialization complete", { component: "AuthProvider" });
      }
    }
  }, []);

  useEffect(() => {
    void initAuth();
    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
    };
  }, [initAuth]);

  const login = async (credentials: AuthenticateUserRequest): Promise<void> => {
    const result = await authService.login(credentials);
    if (result.succeeded && result.user) {
      setUser(result.user);
      logger.debug("AuthProvider - setUser called after login", {
        component: "AuthProvider",
        user: result.user.email,
        isAuthenticated: true,
      });
    } else {
      throw new Error(result.errors?.join(", ") || "Login failed");
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      logger.info('Logout successful on the server.', { component: 'AuthProvider' });
    } catch (error) {
      logger.error('Server logout failed', error as Error, { component: 'AuthProvider' });
    } finally {
      setUser(null);
      logger.info('Client-side user state cleared.', { component: 'AuthProvider' });
    }
  };

  // Listen for session expired events from API interceptors
  useEffect(() => {
    const handleSessionExpired = (event: CustomEvent<SessionExpiredDetail>) => {
      logger.warn('Session expired event received, clearing user state and navigating to login', {
        component: 'AuthProvider',
        redirectPath: event.detail.redirectPath,
      });

      setUser(null);

      const redirectPath = event.detail.redirectPath;
      if (redirectPath && redirectPath !== ROUTES.login && !redirectPath.startsWith(ROUTES.login)) {
        void navigate(`${ROUTES.login}?redirect=${encodeURIComponent(redirectPath)}`);
      } else {
        void navigate(ROUTES.login);
      }
    };

    window.addEventListener('auth:sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('auth:sessionExpired', handleSessionExpired);
    };
  }, [navigate]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isLoading,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
